import './OrdersTable.scss';

import React, { FunctionComponent, ReactElement, useEffect, useState } from 'react';
import Accordion from 'react-bootstrap/Accordion';
import { ReactComponent as RefundIcon } from '../../../assets/refund.svg';

import DiceTable, {
    DiceTableRowInterface,
    formattedDateCell,
    notApplicableText,
    unavailableText,
    valueOrUnavailable,
} from '../../shared/components/DiceTable';
import OrderIcon from './OrderIcon';
import { OrderModel, OrderType } from './types';
import EShopOrderOverlay from '../../shared/components/eshop-order-overlay/EShopOrderOverlay';
import { RadioPill } from '../../shared/components/pill-button/RadioPill';
import Utils from '../../../shared/Utils';
import { Pagination, PaginationInterface } from '../../shared/components/pagination/Pagination';
import { SearchField } from '../../shared/components/search-field/SearchField';
import { formatCurrency, formatDate } from './RefundFormat';
import { ReactComponent as CaretUp } from '../../../assets/caret-up.svg';
import { ReactComponent as CaretDown } from '../../../assets/caret-down.svg';

// If there are no items, return 1 to ensure the row is rendered with a minimum span.
const getRowSpan = (order: OrderModel): number => {
    if (order.items && order.items.length > 0) {
        return order.items.length;
    }
    return 1;
};

const orderNumberColumn = (order: OrderModel): ReactElement => (
    <div className="d-flex justify-content-between align-items-center">
        {order.id ? (
            <EShopOrderOverlay
                subType={order.subType}
                orderNumber={order.id.toString()}
                orderSource={order.orderSource}
                channel={order.channel ?? ''}
                contentType={order.contentType ?? -1}
            >
                <span>{order.id}</span>
            </EShopOrderOverlay>
        ) : (
            unavailableText
        )}
        {order.id && (
            <OrderIcon
                orderType={order.type}
                orderSubType={order.subType}
                isGuestOrder={order.isGuestOrder}
            />
        )}
    </div>
);
const dateShippedColumnArray = (order: OrderModel): ReactElement[] => {
    if (order.items?.length) {
        return order.items.map((item, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <div key={`shipped-${item.trackingNumber}-${index}`}>
                {formattedDateCell(
                    item.shipDate,
                    order.type === OrderType.PHYSICAL ? unavailableText : notApplicableText,
                )}
            </div>
        ));
    }
    return [unavailableText];
};

const statusColumnArray = (
    order: OrderModel,
    openKeys: { [key: string]: boolean },
    handleToggle: (id: string) => void,
): ReactElement[] => {
    if (!order.items?.length) {
        return [
            <div key="na">
                {order.type === OrderType.PHYSICAL ? unavailableText : notApplicableText}
            </div>,
        ];
    }
    return order.items.map((item, index) => {
        const statusText =
            item.orderStatus ||
            (order.type === OrderType.PHYSICAL ? unavailableText : notApplicableText);
        const key = `status-${order.id}-${index}`;
        if (item.trackingNumber && item.trackingUrl) {
            return (
                <Accordion
                    className="orders-table__cell-accordion"
                    activeKey={openKeys[key] ? key : undefined}
                    key={key}
                >
                    <button
                        type="button"
                        className="status-toggle"
                        onClick={() => handleToggle(key)}
                    >
                        {statusText}
                        {openKeys[key] ? <CaretUp /> : <CaretDown />}
                    </button>
                    <Accordion.Collapse eventKey={key}>
                        <>
                            <hr />
                            <div>
                                <a
                                    href={item.trackingUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {item.trackingNumber}
                                </a>
                            </div>
                        </>
                    </Accordion.Collapse>
                </Accordion>
            );
        }
        return (
            <div key={key}>
                <span>{statusText}</span>
            </div>
        );
    });
};

const orderTotalColumn = (
    model: OrderModel,
    openKeys: { [key: string]: boolean },
    handleToggle: (id: string) => void,
): ReactElement => {
    if (!model.refundAmount) {
        return (
            <div className="orders-table__order-total-cell">
                <span>{formatCurrency(model.totalAmount)}</span>
            </div>
        );
    }
    return (
        <Accordion
            className="orders-table__cell-accordion"
            activeKey={openKeys[model.id] ? (model.id as string) : undefined}
        >
            <button type="button" onClick={() => handleToggle(model.id as string)}>
                <RefundIcon />
                <span className="ml-2">{formatCurrency(model.totalAmount)}</span>
                {openKeys[model.id] ? <CaretUp /> : <CaretDown />}
            </button>
            <Accordion.Collapse eventKey={model.id as string}>
                <>
                    <hr />
                    <div>Refund Total: {formatCurrency(model.refundAmount)}</div>
                    <div>Refund Date: {formatDate(model.refundDate)}</div>
                </>
            </Accordion.Collapse>
        </Accordion>
    );
};

const itemsColumnArray = (model: OrderModel): ReactElement[] => {
    if (model?.items?.length) {
        return model.items.map((x) => (
            <div key={`item-${model.id}`}>{x.description || unavailableText}</div>
        ));
    }
    return [unavailableText];
};

export interface OrdersTableProps {
    orders: OrderModel[];
    orderNumber?: string;
}

const OrdersTable: FunctionComponent<OrdersTableProps> = ({ orders, orderNumber }) => {
    const [searchTerm, setSearchTerm] = useState<string>(orderNumber || '');
    const [filter, setFilter] = useState<string | null>('all');
    const [filteredOrders, setFilteredOrders] = useState<OrderModel[]>(orders);

    const [accordionKeys, setAccordionKeys] = useState<{ [key: string]: boolean }>({});

    const handleAccordionToggle = (key: string) => {
        setAccordionKeys((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    const [pagination, setPagination] = useState<PaginationInterface>({
        totalPages: 1,
        currentPage: 1,
        pageSize: 5,
        numItems: 0,
    });
    const pageStartIndex = (pagination.currentPage - 1) * pagination.pageSize;
    const pageEndIndex = pageStartIndex + pagination.pageSize;

    useEffect(() => {
        if (filter === 'all') {
            setFilteredOrders(orders.filter(applySearch));
        } else {
            setFilteredOrders(orders.filter(applyFilter).filter(applySearch));
        }
    }, [filter, searchTerm, orders]);

    useEffect(() => {
        setPagination({
            ...pagination,
            totalPages: Math.ceil(filteredOrders.length / pagination.pageSize),
            currentPage: 1,
            numItems: filteredOrders.length,
        });
    }, [filteredOrders]);

    const applyFilter = (order: OrderModel) => order.type === filter;
    const applySearch = (order: OrderModel) =>
        Utils.searchFields(searchTerm, [
            order.id,
            order.type,
            Utils.formatDate(order.date),
            order.country,
        ]) ||
        order.items.some((item) =>
            Utils.searchFields(searchTerm, [
                item.description,
                item.orderStatus,
                Utils.formatDate(item.shipDate),
            ]),
        );

    const count = (orderType: OrderType) =>
        orders.filter(applySearch).filter((order) => order.type === orderType).length;

    const rows: DiceTableRowInterface[] = filteredOrders
        .slice(pageStartIndex, pageEndIndex) // pagination
        .map((order) => ({
            rowSpanByColumnIndex: {
                0: getRowSpan(order),
                1: getRowSpan(order),
                2: getRowSpan(order),
                3: getRowSpan(order),
            },
            columns: [
                orderNumberColumn(order),
                valueOrUnavailable(order.country),
                formattedDateCell(order.date, unavailableText),
                orderTotalColumn(order, accordionKeys, handleAccordionToggle),
                itemsColumnArray(order)[0],
                statusColumnArray(order, accordionKeys, handleAccordionToggle)[0],
                dateShippedColumnArray(order)[0],
            ],
            columnsToNest: [
                itemsColumnArray(order).slice(1),
                statusColumnArray(order, accordionKeys, handleAccordionToggle).slice(1),
                dateShippedColumnArray(order).slice(1),
            ],
        }));

    return (
        <div className="orders-table">
            <fieldset className="orders-table__filter">
                <legend className="orders-table__filter-label">
                    <strong className="mr-2 pt-1">Order Type:</strong>
                </legend>

                <RadioPill
                    id="orders-filter-all"
                    name="orders-filter"
                    className="mr-2"
                    value="all"
                    text="All"
                    active={filter === 'all'}
                    onChange={setFilter}
                />
                <RadioPill
                    id="orders-filter-eshop"
                    name="orders-filter"
                    className="mr-2"
                    value={OrderType.DIGITAL}
                    text={`Digital (${count(OrderType.DIGITAL)})`}
                    disabled={count(OrderType.DIGITAL) === 0}
                    active={filter === OrderType.DIGITAL}
                    onChange={setFilter}
                />
                <RadioPill
                    id="orders-filter-parts"
                    name="orders-filter"
                    value={OrderType.PHYSICAL}
                    text={`Physical (${count(OrderType.PHYSICAL)})`}
                    disabled={count(OrderType.PHYSICAL) === 0}
                    active={filter === OrderType.PHYSICAL}
                    onChange={setFilter}
                />
                <SearchField
                    placeholder="Search orders"
                    onQueryChange={(text) => setSearchTerm(text)}
                    orderNumber={orderNumber}
                />
            </fieldset>
            <DiceTable
                tableName="orders"
                headerNames={[
                    'Order Number',
                    'Country',
                    'Order Date',
                    'Order Total',
                    'Item(s)',
                    'Status',
                    'Date Shipped',
                ]}
                rows={rows}
                noRowsMessage={`We couldn't find a match for "${searchTerm}". Please try again.`}
            />
            <Pagination pagination={pagination} updatePagination={setPagination} />
        </div>
    );
};

export default OrdersTable;
